import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, TextField, InputAdornment, IconButton, CircularProgress,
  Pagination, Chip, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Tooltip, FormControlLabel, Switch, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import { money } from '../utils/quoteMath';

const API = 'https://elnaf.fi/apiElnaf/api/b2b_products.php';

const stockChip = (stock) => {
  if (stock <= 0)  return { label: 'Out',        bg: '#fee2e2', fg: '#991b1b' };
  if (stock === 1) return { label: 'Last one',   bg: '#fee2e2', fg: '#991b1b' };
  if (stock <= 3)  return { label: `${stock} left`, bg: '#ffedd5', fg: '#9a3412' };
  if (stock <= 10) return { label: `${stock}`,   bg: '#fef3c7', fg: '#92400e' };
  return { label: `${stock}`, bg: '#dcfce7', fg: '#166534' };
};

/**
 * Searchable product table. Each row has an inline qty stepper that doubles as
 * the "already in quote" indicator, so the salesperson never loses their place.
 */
const B2BProductList = ({ onAdd, onSetQty, skuQty }) => {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ products: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [addQty, setAddQty] = useState({}); // per-row staged qty before adding

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    let abort = false;
    const run = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page, limit: 25 });
        if (debounced) params.set('q', debounced);
        if (inStockOnly) params.set('inStockOnly', '1');
        const res = await fetch(`${API}?${params}`);
        const json = await res.json();
        if (abort) return;
        setData({
          products: Array.isArray(json.products) ? json.products : [],
          total: json.total || 0,
          pages: json.pages || 1,
        });
      } catch {
        if (!abort) setData({ products: [], total: 0, pages: 1 });
      } finally {
        if (!abort) setLoading(false);
      }
    };
    run();
    return () => { abort = true; };
  }, [debounced, page, inStockOnly]);

  const stageQty = useCallback((sku, v) => {
    setAddQty((s) => ({ ...s, [sku]: Math.max(1, Number(v) || 1) }));
  }, []);

  const handleAdd = useCallback((p) => {
    onAdd(p, addQty[p.SKU] || 1);
    setAddQty((s) => ({ ...s, [p.SKU]: 1 }));
  }, [onAdd, addQty]);

  return (
    <Box>
      {/* Toolbar */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by SKU, brand, description, EAN, category…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 1, minWidth: 280 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={inStockOnly} onChange={(e) => { setInStockOnly(e.target.checked); setPage(1); }} />}
          label={<Typography variant="body2">In stock only</Typography>}
        />
        <Typography variant="caption" sx={{ color: '#6b7280', ml: 'auto' }}>
          <strong style={{ color: '#111827' }}>{data.total.toLocaleString()}</strong> products
        </Typography>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 56 }} />
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }}>SKU / BRAND</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }}>PRODUCT</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }}>CATEGORY</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }}>EAN</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }} align="right">WEIGHT</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }} align="center">STOCK</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280' }} align="right">UNIT PRICE</TableCell>
              <TableCell sx={{ fontWeight: 700, fontSize: 11, color: '#6b7280', width: 190 }} align="center">ADD TO QUOTE</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data.products.length === 0 ? (
              <TableRow><TableCell colSpan={9}>
                <Box display="flex" justifyContent="center" py={6}><CircularProgress size={28} sx={{ color: '#845adf' }} /></Box>
              </TableCell></TableRow>
            ) : data.products.length === 0 ? (
              <TableRow><TableCell colSpan={9}>
                <Box textAlign="center" py={6}>
                  <Typography variant="body2" color="text.secondary">No products match this search.</Typography>
                </Box>
              </TableCell></TableRow>
            ) : (
              data.products.map((p) => {
                const inQuote = skuQty[p.SKU] || 0;
                const sc = stockChip(p.stock);
                const staged = addQty[p.SKU] || 1;
                return (
                  <TableRow key={p.SKU} hover sx={{ backgroundColor: inQuote ? '#faf8ff' : undefined }}>
                    <TableCell>
                      <Box sx={{ width: 40, height: 40, backgroundColor: '#f3f4f6', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {p.photo
                          ? <img src={p.photo} alt={p.SKU} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
                          : <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 9 }}>—</Typography>}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 12 }}>{p.SKU}</Typography>
                      <Typography variant="caption" sx={{ color: '#6b7280' }}>{p.brand}</Typography>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={p.description || ''}>
                        <Typography variant="body2" sx={{ fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.description || p.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell sx={{ maxWidth: 140 }}>
                      <Tooltip title={p.category || ''}>
                        <Typography variant="caption" sx={{ color: '#4b5563', fontSize: 10.5, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.category || '—'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 10.5, fontFamily: 'monospace' }}>
                        {p.ean || '—'}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <Typography variant="caption" sx={{ color: '#6b7280', fontSize: 10.5 }}>
                        {p.weight > 0 ? `${p.weight} kg` : '—'}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip label={sc.label} size="small" sx={{ height: 20, fontSize: 10, fontWeight: 600, backgroundColor: sc.bg, color: sc.fg }} />
                    </TableCell>

                    <TableCell align="right">
                      {p.available
                        ? <Typography variant="body2" sx={{ fontWeight: 700 }}>{money(p.price)}</Typography>
                        : <Tooltip title="No price set — cannot be quoted"><Chip label="No price" size="small" sx={{ height: 20, fontSize: 10, backgroundColor: '#f3f4f6', color: '#6b7280' }} /></Tooltip>}
                    </TableCell>

                    <TableCell align="center">
                      {inQuote > 0 ? (
                        // Already in the quote → edit its qty straight from the catalog
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => onSetQty(p.SKU, inQuote - 1)} disabled={inQuote <= 1}><RemoveIcon fontSize="small" /></IconButton>
                          <TextField
                            size="small" type="number" value={inQuote}
                            onChange={(e) => onSetQty(p.SKU, e.target.value)}
                            sx={{ width: 62 }} inputProps={{ min: 1, style: { textAlign: 'center', padding: '6px 4px', fontWeight: 700 } }}
                          />
                          <IconButton size="small" onClick={() => onSetQty(p.SKU, inQuote + 1)}><AddIcon fontSize="small" /></IconButton>
                          <Tooltip title="In quote"><CheckIcon fontSize="small" sx={{ color: '#16a34a', ml: 0.5 }} /></Tooltip>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75 }}>
                          <TextField
                            size="small" type="number" value={staged}
                            onChange={(e) => stageQty(p.SKU, e.target.value)}
                            disabled={!p.available}
                            sx={{ width: 62 }} inputProps={{ min: 1, style: { textAlign: 'center', padding: '6px 4px' } }}
                          />
                          <Button
                            size="small" variant="contained" startIcon={<AddIcon />} disabled={!p.available}
                            onClick={() => handleAdd(p)}
                            sx={{ backgroundColor: '#845adf', textTransform: 'none', minWidth: 74, '&:hover': { backgroundColor: '#6c48c5' } }}
                          >
                            Add
                          </Button>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {data.pages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={data.pages} page={page} onChange={(_, p) => setPage(p)} color="primary" shape="rounded" size="small" />
        </Box>
      )}
    </Box>
  );
};

export default B2BProductList;
